import { c as _c } from "react/compiler-runtime";
import { Stack } from '@mui/material';
import { GridRowParams } from '@mui/x-data-grid-pro';
import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { Alert } from '@/components/notifications/Alert/Alert';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TableEmptyState } from '@/components/tables/DataTable/TableEmptyState';
import { Column } from '@/components/tables/DataTable/types';
import { useRequiredParam } from '@/hooks/useRequiredParam';
import { ROUTE_KEYS } from '@/navigation/constants';
import { formatDateToMonDDYYYYWithTime } from '@/utils/formatting/dates';
import { useAdminFormTestHistoryQuery } from './AdminFormTests.generated';
interface TestHistoryRow {
  id: string;
  version: string;
  createdAt: string;
}
const columns: Column<TestHistoryRow>[] = [{
  field: 'createdAt',
  headerName: 'Test run',
  flex: 1,
  renderCell: TextRenderer()
}, {
  field: 'version',
  headerName: 'Snapshot version',
  flex: 1,
  renderCell: TextRenderer()
}];
export function AdminFormTestsPage() {
  const $ = _c(29);
  const formId = useRequiredParam("formId");
  const {
    navigate
  } = useNavigateToRoute();
  let t0;
  if ($[0] !== formId) {
    t0 = {
      variables: {
        id: formId
      },
      fetchPolicy: "network-only"
    };
    $[0] = formId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const history = useAdminFormTestHistoryQuery(t0);
  const versions = history.data?.node?.__typename === "Form" ? history.data.node.versions : undefined;
  let t1;
  if ($[2] !== versions?.edges) {
    t1 = versions?.edges ?? [];
    $[2] = versions?.edges;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t1) {
    t2 = t1.flatMap(_temp);
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const rows = t2;
  let t3;
  if ($[6] !== formId || $[7] !== navigate) {
    t3 = <Button size="sm" variant="secondary" onClick={() => navigate(ROUTE_KEYS.ADMIN_FORM_EDITOR, {
      formId
    })}>Back to editor</Button>;
    $[6] = formId;
    $[7] = navigate;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== history.error) {
    t4 = history.error && <Alert severity="error">Test runs could not be loaded.</Alert>;
    $[9] = history.error;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      noRowsOverlay: _temp2
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== formId || $[13] !== navigate) {
    t6 = params => navigate(ROUTE_KEYS.ADMIN_FORM_TEST_RUN, {
      formId,
      versionId: params.row.id
    });
    $[12] = formId;
    $[13] = navigate;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== history.loading || $[16] !== rows || $[17] !== t6) {
    t7 = <DataTableCard><DataTable columns={columns} rows={rows} loading={history.loading} pagination={false} slots={t5} onRowClick={t6} /></DataTableCard>;
    $[15] = history.loading;
    $[16] = rows;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== history || $[20] !== versions) {
    t8 = versions?.pageInfo.hasNextPage && <Button size="sm" variant="secondary" disabled={history.loading} onClick={async () => {
      await history.fetchMore({
        variables: {
          after: versions.pageInfo.endCursor
        },
        updateQuery: _temp3
      });
    }}>Load older runs</Button>;
    $[19] = history;
    $[20] = versions;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t4 || $[23] !== t7 || $[24] !== t8) {
    t9 = <Stack spacing={2}>{t4}{t7}{t8}</Stack>;
    $[22] = t4;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t3 || $[27] !== t9) {
    t10 = <SubpageLayout heading="Test runs" actions={t3}>{t9}</SubpageLayout>;
    $[26] = t3;
    $[27] = t9;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp3(previous, t0) {
  const {
    fetchMoreResult
  } = t0;
  if (previous.node?.__typename !== "Form" || fetchMoreResult.node?.__typename !== "Form") {
    return previous;
  }
  return {
    node: {
      ...fetchMoreResult.node,
      versions: {
        ...fetchMoreResult.node.versions,
        edges: [...(previous.node.versions.edges ?? []), ...(fetchMoreResult.node.versions.edges ?? [])]
      }
    }
  };
}
function _temp2() {
  return <TableEmptyState text="No test runs yet" />;
}
function _temp(edge) {
  return edge?.node ? [{
    id: edge.node.id,
    version: String(edge.node.version),
    createdAt: formatDateToMonDDYYYYWithTime(edge.node.createdAt)
  }] : [];
}